/**
 * Contact component for Manatee Labs website.
 * Follows the design specifications from manatee-labs-full-spec.md
 */
import { useState } from "react";
import emailjs from "@emailjs/browser";
import Snackbar from "@mui/material/Snackbar";
import Alert, { AlertColor } from "@mui/material/Alert";
import { motion } from "framer-motion";
import "./contact.css";

export interface ContactDataFormat {
  address: string;
  phone: string;
  email: string;
  linkedin: string;
  twitter: string;
  medium: string;
}

export interface ContactProps {
  data: ContactDataFormat | undefined;
}

interface ContactState {
  name: string;
  email: string;
  message: string;
  openSnackbar: boolean;
  snackBarSeverity: AlertColor | undefined;
  snackbarMsg: string;
}

const initialState: ContactState = {
  name: "",
  email: "",
  message: "",
  openSnackbar: false,
  snackBarSeverity: undefined,
  snackbarMsg: "",
};

export const Contact = (props: ContactProps) => {
  const [
    { name, email, message, openSnackbar, snackBarSeverity, snackbarMsg },
    setState,
  ] = useState(initialState);

  // Animation variants for fade-in effect
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // Close snackbar when requested
  const handleSnackbarClose = () => {
    clearState();
    console.log("Close snackbar.");
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const clearState = () => setState({ ...initialState });

  /**
   * Send email to hello@manateelabsusa.com
   * @param e
   */
  const handleSubmit = (e: any) => {
    e.preventDefault();
    console.log(name, email, message);

    // Create template parameters
    const templateParams = {
      message: message,
      to_name: "Manatee Labs",
      name: name,
      email: email,
    };

    // Use the direct send method instead of sendForm
    emailjs
      .send(
        "service_aq8i1yc",
        "template_jzsk33s",
        templateParams,
        "Bm2fPDfgw3mNfita-"
      )
      .then(
        (result) => {
          console.log(result.text);
          setState({
            name,
            email,
            message,
            openSnackbar: true,
            snackbarMsg: "Message sent successfully",
            snackBarSeverity: "success",
          });
          clearState();
        },
        (error) => {
          console.log(error.text);
          setState({
            name,
            email,
            message,
            openSnackbar: true,
            snackbarMsg:
              "Sorry! There was a problem. Please try again or reach out directly to hello@manateelabsusa.com",
            snackBarSeverity: "error",
          });
        }
      );
  };

  /**
   * Validate that the contact form is ready to be submitted
   * @returns boolean
   */
  const isFormValid = (): boolean => {
    if (name !== "" && email !== "" && message !== "") {
      return true;
    } else {
      return false;
    }
  };

  return (
    <div id="contact">
      <div className="container">
        <motion.div
          className="contact-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="contact-title">Let's Talk Amazon Connect</h2>
          <p className="contact-subtitle">
            Ready to transform your contact center? We're here to help you build
            a modern, AI-powered solution on Amazon Connect.
          </p>
        </motion.div>

        <div className="row">
          <motion.div
            className="contact-form-container"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.2 },
              },
            }}
          >
            <form name="sentMessage" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  placeholder="Your Name"
                  required
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  placeholder="Your Email"
                  required
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  id="message"
                  className="form-control"
                  rows={5}
                  placeholder="How can we help with your Amazon Connect project?"
                  required
                  onChange={handleChange}
                ></textarea>
              </div>
              <motion.button
                type="submit"
                className="btn-submit"
                disabled={!isFormValid()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Schedule a Consultation
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        onClose={handleSnackbarClose}
        autoHideDuration={6000}
        message={snackbarMsg}
        key="bottom-center"
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackBarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};
